// src/components/ListMetricsBar.jsx
import React, { useState, useEffect } from 'react';

const STORAGE_KEYS = {
  SELECTED_METRICS: 'list_selected_metrics',
  TAX_RATE: 'list_tax_rate',
  BUDGET: 'list_budget'
};

// Valores por defecto
const DEFAULT_TAX_RATE = 9.5;
const DEFAULT_BUDGET = 100.00;

// Índices de métricas (para ordenamiento fijo)
const METRICS = [
  { id: 'inList', label: 'In list', type: 'currency' },
  { id: 'inListPlusTax', label: 'In list + taxes', type: 'currency' },
  { id: 'checked', label: 'Checked', type: 'currency' },
  { id: 'checkedPlusTax', label: 'Checked + taxes', type: 'currency' },
  { id: 'unchecked', label: 'Unchecked', type: 'currency' },
  { id: 'uncheckedPlusTax', label: 'Unchecked + taxes', type: 'currency' },
  { id: 'checkedVsBudget', label: 'Checked vs Budget', type: 'currencyNegative' },
  { id: 'uncheckedVsBudget', label: 'Unchecked vs Budget', type: 'currencyNegative' },
  { id: 'inListVsBudget', label: 'In list vs Budget', type: 'currencyNegative' }
];

// Selección por defecto (In list, Checked + taxes, Checked vs Budget)
const DEFAULT_SELECTED_METRICS = ['inList', 'checkedPlusTax', 'checkedVsBudget'];

const ListMetricsBar = ({ items, onOpenModal }) => {
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE);
  const [budget, setBudget] = useState(DEFAULT_BUDGET);

  // Cargar desde localStorage al montar
  useEffect(() => {
    const savedMetrics = localStorage.getItem(STORAGE_KEYS.SELECTED_METRICS);
    if (savedMetrics) {
      try {
        const parsed = JSON.parse(savedMetrics);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedMetrics(parsed);
        } else {
          setSelectedMetrics(DEFAULT_SELECTED_METRICS);
        }
      } catch (e) {
        setSelectedMetrics(DEFAULT_SELECTED_METRICS);
      }
    } else {
      setSelectedMetrics(DEFAULT_SELECTED_METRICS);
    }

    const savedTaxRate = localStorage.getItem(STORAGE_KEYS.TAX_RATE);
    if (savedTaxRate) {
      const parsed = parseFloat(savedTaxRate);
      if (!isNaN(parsed) && parsed >= 0) {
        setTaxRate(parsed);
      }
    }

    const savedBudget = localStorage.getItem(STORAGE_KEYS.BUDGET);
    if (savedBudget) {
      const parsed = parseFloat(savedBudget);
      if (!isNaN(parsed) && parsed >= 0) {
        setBudget(parsed);
      }
    }
  }, []);

  // Calcular valores de las métricas
  const calculateMetrics = () => {
    // Calcular totales base
    let inListTotal = 0;
    let checkedTotal = 0;
    let uncheckedTotal = 0;

    items.forEach(item => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      const total = price * quantity;
      const status = parseInt(item.status);

      inListTotal += total;
      
      if (status === 1) {
        checkedTotal += total;
      } else {
        uncheckedTotal += total;
      }
    });

    const taxMultiplier = 1 + (taxRate / 100);
    
    const inListPlusTax = inListTotal * taxMultiplier;
    const checkedPlusTax = checkedTotal * taxMultiplier;
    const uncheckedPlusTax = uncheckedTotal * taxMultiplier;
    
    const inListVsBudget = budget - inListPlusTax;
    const checkedVsBudget = budget - checkedPlusTax;
    const uncheckedVsBudget = budget - uncheckedPlusTax;

    return {
      inList: inListTotal,
      inListPlusTax,
      checked: checkedTotal,
      checkedPlusTax,
      unchecked: uncheckedTotal,
      uncheckedPlusTax,
      checkedVsBudget,
      uncheckedVsBudget,
      inListVsBudget
    };
  };

  const metrics = calculateMetrics();

  // Formatear moneda
  const formatCurrency = (value) => {
    return `$${Math.abs(value).toFixed(2)}`;
  };

  // Renderizar una métrica individual
  const renderMetric = (metricId) => {
    const metric = METRICS.find(m => m.id === metricId);
    if (!metric) return null;
    
    const value = metrics[metricId];
    const isNegative = value < 0;
    const isNegativeType = metric.type === 'currencyNegative';
    
    // Solo aplicar color rojo si es tipo currencyNegative Y el valor es negativo
    const textColorClass = (isNegativeType && isNegative) ? 'text-red-600' : 'text-gray-900';
    
    return (
      <div key={metricId} className="flex flex-col px-1 py-1 w-auto">
        <span className="text-xs text-purple-600 whitespace-nowrap">{metric.label}</span>
        <span className={`text-base font-bold ${textColorClass} whitespace-nowrap`}>
          {isNegative && isNegativeType ? '-' : ''}{formatCurrency(value)}
        </span>
      </div>
    );
  };

  // Si no hay métricas seleccionadas, no mostrar nada
  if (selectedMetrics.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border-t border-gray-200 px-1 py-2">
      <div className="flex items-center justify-between">
        {/* Métricas seleccionadas - orden fijo según METRICS */}
        <div className="flex-1 flex items-center justify-around overflow-x-auto">
          {METRICS.filter(m => selectedMetrics.includes(m.id)).map(metric => 
            renderMetric(metric.id)
          )}
        </div>
        
        {/* Engrane morado para abrir modal */}
        <button
          onClick={onOpenModal}
          className="ml-2 w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-purple-200 transition-colors flex-shrink-0"
          aria-label="Configure metrics"
        >
          <i className="fas fa-cog text-purple-600 text-sm"></i>
        </button>
      </div>
    </div>
  );
};

// Exportar también las constantes para usar en el modal
export { METRICS, DEFAULT_SELECTED_METRICS, DEFAULT_TAX_RATE, DEFAULT_BUDGET, STORAGE_KEYS };
export default ListMetricsBar;